import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, FolderOpen } from "lucide-react";
import { http, apiError } from "@/lib/api";
import AdminShell, { PageHead } from "@/components/AdminShell";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";
import MasterDataTransfer from "@/components/MasterDataTransfer";
import { Toggle } from "@/pages/admin/Products";

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const isOwner = me?.role === "owner";

  async function load() {
    try {
      const [categoriesRes, meRes] = await Promise.all([
        http.get("/admin/categories"),
        http.get("/auth/me"),
      ]);

      setCats(categoriesRes.data);
      setMe(meRes.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function add() {
    if (!isOwner) {
      toast.error("Hanya pemilik yang boleh menambah kategori.");
      return;
    }

    const cleanName = name.trim();

    if (!cleanName) {
      toast.error("Nama kategori wajib diisi.");
      return;
    }

    try {
      await http.post("/admin/categories", {
        name: cleanName,
      });

      setName("");
      toast.success("Kategori ditambahkan");
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  async function toggle(c) {
    if (!isOwner) {
      toast.error("Hanya pemilik yang boleh mengubah kategori.");
      return;
    }

    try {
      await http.patch(`/admin/categories/${c.id}`, {
        active: !c.active,
      });

      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
  }

  function askDelete(category) {
    if (!isOwner) {
      toast.error("Hanya pemilik yang boleh menghapus kategori.");
      return;
    }
    setDeleteTarget(category);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await http.delete(`/admin/categories/${deleteTarget.id}`);
      toast.success("Kategori dihapus");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <AdminShell>
      <PageHead
        eyebrow="Katalog"
        title="Kategori"
        desc={
          isOwner
            ? "Kelompokkan produk agar mudah dicari."
            : "Lihat daftar kategori produk."
        }
      >
        {isOwner && (
          <div className="flex flex-wrap items-center gap-2">
            <MasterDataTransfer
              kind="categories"
              label="Kategori"
              onImported={load}
            />
          </div>
        )}
      </PageHead>

      {isOwner && (
        <div className="mb-5 flex gap-2">
          <input
            data-testid="category-name-input"
            value={name}
            onChange={(e) => {
              const value = e.target.value
                .toLowerCase()
                .replace(/(^|\s)\S/g, (char) => char.toUpperCase());

              setName(value);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                add();
              }
            }}
            placeholder="Nama kategori baru"
            className="flex-1 rounded-xl border border-border bg-muted px-4 py-3 text-sm outline-none"
          />

          <button
            data-testid="add-category-btn"
            onClick={add}
            className="flex items-center gap-2 rounded-xl bg-primary px-4 font-bold text-primary-foreground"
          >
            <Plus size={16} />
          </button>
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
          Memuat kategori...
        </div>
      ) : (
        <div className="space-y-2">
          {cats.map((c) => (
            <div
              key={c.id}
              className="flex items-center justify-between rounded-2xl border border-border bg-card p-4"
            >
              <div className="flex items-center gap-3">
                <div className="grid h-9 w-9 place-items-center rounded-xl bg-primary/15 text-primary">
                  <FolderOpen size={16} />
                </div>

                <div>
                  <b className="text-sm text-card-foreground">
                    {c.name}
                  </b>

                  {!isOwner && (
                    <div className="mt-0.5 text-[11px] text-muted-foreground">
                      {c.active ? "Aktif" : "Nonaktif"}
                    </div>
                  )}
                </div>
              </div>

              {isOwner && (
                <div className="flex items-center gap-3">
                  <Toggle
                    label=""
                    checked={c.active}
                    onChange={() => toggle(c)}
                  />

                  <button
                    data-testid={`del-category-${c.id}`}
                    onClick={() => askDelete(c)}
                    className="grid h-9 w-9 place-items-center rounded-xl bg-destructive/10 text-destructive"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              )}
            </div>
          ))}

          {cats.length === 0 && (
            <div className="rounded-2xl border border-dashed border-border p-10 text-center text-sm text-muted-foreground">
              Belum ada kategori.
            </div>
          )}
        </div>
      )}

      <ConfirmDeleteModal
        open={Boolean(deleteTarget)}
        title="Hapus Kategori?"
        message="Kategori hanya dapat dihapus jika tidak memiliki produk."
        itemName={deleteTarget?.name || ""}
        busy={deleteBusy}
        onCancel={() => !deleteBusy && setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </AdminShell>
  );
}
